import React, { useEffect, useState } from "react";
import { PageContainer } from "../../components/common/PageContainer/PageContainer.styles";
import { eventService } from "../../services/api/eventService";
import {
  EventsSection,
  CalendarContainer,
  CalendarCard,
  ContentWrapper,
  WhiteBackgroundWrapper,
  EventImage,
  ImageContainer,
  ImageErrorOverlay,
  DownloadOverlay,
  DownloadButton,
  LoadingSpinner,
  NoEventsMessage
} from "./Events.styles";

import { Event, EventLink } from "../../types/event";
import { toast } from "react-toastify";
import { FaDownload } from "react-icons/fa";

// Function to detect and parse links from text
const parseLinksFromText = (text: string): { text: string; links: EventLink[] } => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const links: EventLink[] = [];
  let match;
  let processedText = text;

  // Reset regex
  urlRegex.lastIndex = 0;

  while ((match = urlRegex.exec(text)) !== null) {
    const url = match[0];
    const urlType = detectLinkType(url);
    const label = getLinkLabel(text, match.index);
    
    links.push({
      url,
      type: urlType,
      label: label || getDefaultLabel(urlType),
      icon: getLinkIcon(urlType)
    });

    // Replace the URL in the text with a placeholder
    processedText = processedText.replace(url, `[LINK_${links.length - 1}]`);
  }

  return { text: processedText, links };
};

// Function to detect the type of link
const detectLinkType = (url: string): EventLink['type'] => {
  const urlLower = url.toLowerCase();
  
  if (urlLower.includes('zoom.us')) return 'zoom';
  if (urlLower.includes('meet.google.com')) return 'meet';
  if (urlLower.includes('teams.microsoft.com')) return 'teams';
  if (urlLower.includes('webinar')) return 'webinar';
  if (urlLower.includes('register') || urlLower.includes('registration')) return 'registration';
  return 'default';
};

// Function to get a default label based on link type
const getDefaultLabel = (type: EventLink['type']): string => {
  switch (type) {
    case 'zoom': return 'Join Zoom Meeting';
    case 'meet': return 'Join Google Meet';
    case 'teams': return 'Join Teams Meeting';
    case 'webinar': return 'Join Webinar';
    case 'registration': return 'Register Here';
    default: return 'Visit Link';
  }
};

// Function to get an icon for the link type
const getLinkIcon = (type: EventLink['type']): string => {
  switch (type) {
    case 'zoom': return '🎥';
    case 'meet': return '👥';
    case 'teams': return '💼';
    case 'webinar': return '📺';
    case 'registration': return '📝';
    default: return '🔗';
  }
};

// Function to extract a label from the text around the URL
const getLinkLabel = (text: string, urlIndex: number): string | null => {
  const beforeUrl = text.substring(Math.max(0, urlIndex - 50), urlIndex);
  const labelMatch = beforeUrl.match(/([^.!?]+(?:link|join|register|meeting|webinar)[^.!?]*)/i);
  return labelMatch ? labelMatch[0].trim() : null;
};

const Events: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});

  // Function to get proxied image URL for display with CORS fallback
  const getDisplayImageUrl = (s3Url: string) => {
    if (!s3Url) return '';
    
    try {
      // If the URL is already a proxy URL, return it as is
      if (s3Url.includes('/api/images/proxy')) {
        return s3Url;
      }

      // For production, due to CORS issues, use direct S3 URLs
      // This avoids the blinking/reloading issue
      if (process.env.NODE_ENV === 'production') {
        return s3Url; // Use direct S3 URL in production
      }
      
      // For development, also use direct S3 URL for simplicity
      return s3Url;
    } catch (error) {
      return s3Url; // Fallback to original URL if parsing fails
    }
  };

  // Function to get proxied image URL for downloads (always uses proxy)
  const getProxiedImageUrl = (s3Url: string) => {
    if (!s3Url) return '';
    
    try {
      // If the URL is already a proxy URL, return it as is
      if (s3Url.includes('/api/images/proxy')) {
        return s3Url;
      }

      // Extract the key from the S3 URL
      const urlObj = new URL(s3Url);
      const key = urlObj.pathname.substring(1); // Remove leading slash
      
      // Use the backend API endpoint to proxy the image
      const apiUrl = import.meta.env.VITE_API_ENDPOINT || 'https://c59obzfnk1.execute-api.us-east-1.amazonaws.com/prod';
      if (!apiUrl) {
        return s3Url; // Fallback to original URL if API URL is not configured
      }
      
      // Always use the proxy URL for downloads
      return `${apiUrl}/api/images/proxy?key=${encodeURIComponent(key)}`;
    } catch (error) {
            return s3Url; // Fallback to original URL if parsing fails
    }
  };

  // Function to test if an image is accessible with CORS fallback
  const testImageAccess = async (url: string): Promise<boolean> => {
    try {
      // For development, skip the test and assume the image is accessible
      if (process.env.NODE_ENV === 'development') {
        return true;
      }

      const proxiedUrl = getProxiedImageUrl(url);
      const response = await fetch(proxiedUrl, { 
        method: 'HEAD',
        mode: 'cors',
        credentials: 'omit'
      });
      return response.ok;
    } catch (error) {
      // If CORS fails, try direct S3 URL as fallback
      try {
        const response = await fetch(url, { 
          method: 'HEAD',
          mode: 'cors',
          credentials: 'omit'
        });
        return response.ok;
      } catch (fallbackError) {
        return false;
      }
    }
  };

  const renderDescription = (description: string) => {
    const { text, links } = parseLinksFromText(description);
    const parts = text.split(/\[LINK_\d+\]/);

    return (
      <div className="description-content">
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            <p>{part}</p>
            {links[index] && (
              <a
                href={links[index].url}
                target="_blank"
                rel="noopener noreferrer"
                className={`event-link ${links[index].type}`}
              >
                {links[index].icon && <span className="link-icon">{links[index].icon}</span>}
                {links[index].label}
              </a>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  // Function to handle image download
  const handleDownloadImage = async (imageUrl: string) => {
    if (!imageUrl) {
      toast.error('No image available to download');
      return;
    }

    try {
      // Show loading notification
      const loadingToast = toast.loading('Downloading image...');

      // Fetch the image
      const response = await fetch(imageUrl, { mode: 'cors' });
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }

      // Convert to blob
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = blobUrl;
      
      // Extract filename from URL or use a default name
      const filename = imageUrl.split('/').pop() || 'event-image.jpg';
      link.download = filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      window.URL.revokeObjectURL(blobUrl);

      // Show success message
      toast.dismiss(loadingToast);
      toast.success('Image downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download image. Please try again.');
    }
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        // Check if API endpoint is configured
        if (!import.meta.env.VITE_API_ENDPOINT) {
          setEvents([]);
          setLoading(false);
          return;
        }

        const data = await eventService.getEvents();
        
        // Sort events by date
        const sortedEvents = data.sort((a, b) => 
          new Date(a.date).getTime() - new Date(b.date).getTime()
        );
        
        // Check image accessibility
        for (const event of sortedEvents) {
          if (event.imageUrl) {
            const isAccessible = await testImageAccess(event.imageUrl);
            if (!isAccessible) {
              setFailedImages(prev => ({ ...prev, [event.id]: true }));
            }
          }
        }
        setEvents(sortedEvents);
      } catch (err) {
        setError("Unable to load events. Please try again later.");
        toast.error("Failed to load events. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  if (loading) {
    return (
      <PageContainer>
        <WhiteBackgroundWrapper>
          <ContentWrapper>
            <LoadingSpinner />
          </ContentWrapper>
        </WhiteBackgroundWrapper>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <WhiteBackgroundWrapper>
          <ContentWrapper>
            <p style={{ color: "red", textAlign: "center" }}>{error}</p>
          </ContentWrapper>
        </WhiteBackgroundWrapper>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <WhiteBackgroundWrapper>
        <ContentWrapper>
          <EventsSection>
            <h2>Upcoming Events</h2>
            <p>
              Explore our exciting events and find out how you can get involved. Together, we can make a lasting impact.
            </p>

            {events.length === 0 ? (
              <NoEventsMessage>
                No upcoming events at the moment. Please check back later!
              </NoEventsMessage>
            ) : (
              <CalendarContainer>
                {events.map((event) => {
                  const eventDate = new Date(event.date);
                  const day = eventDate.getDate();
                  const month = eventDate.toLocaleString("default", { month: "short" });


                  return (
                    <CalendarCard key={event.id}>
                      <div className="date-container">
                        <h2>{day}</h2>
                        <p>{month}</p>
                      </div>

                      <ImageContainer>
                        <EventImage
                          src={event.imageUrl ? getDisplayImageUrl(event.imageUrl) : ''}
                          alt={event.title}
                          onError={() => {
                            // Mark this image as failed for UI purposes
                            if (event.imageUrl) {
                              setFailedImages(prev => ({ ...prev, [event.imageUrl!]: true }));
                            }
                          }}
                          onLoad={() => {
                            // Mark image as successfully loaded
                            if (event.imageUrl) {
                              setFailedImages(prev => ({ ...prev, [event.imageUrl!]: false }));
                            }
                          }}
                        />
                        {event.imageUrl && !failedImages[event.imageUrl] && (
                          <DownloadOverlay>
                            <DownloadButton
                              onClick={() => event.imageUrl && handleDownloadImage(event.imageUrl)}
                              title="Download image"
                            >
                              <FaDownload size={20} />
                            </DownloadButton>
                          </DownloadOverlay>
                        )}
                        {event.imageUrl && failedImages[event.imageUrl] && (
                          <ImageErrorOverlay>
                            <p>Image failed to load</p>
                          </ImageErrorOverlay>
                        )}
                      </ImageContainer>

                      <div className="event-details">
                        <h2>{event.title}</h2>
                        <p>
                          <strong>Date:</strong> {eventDate.toLocaleDateString()} {event.time ? `at ${event.time}` : ""}
                        </p>
                        {event.location && (
                          <p>
                            <strong>Location:</strong> {event.location}
                          </p>
                        )}
                        {event.organizer && (
                          <p>
                            <strong>Organizer:</strong> {event.organizer}
                          </p>
                        )}
                        <p>
                          <strong>Status:</strong>{" "}
                          <span
                            style={{
                              color: event.status === "cancelled" ? "red" :
                                     event.status === "completed" ? "gray" :
                                     "green",
                              fontWeight: "bold",
                            }}
                          >
                            {event.status}
                          </span>
                        </p>
                        {event.description && (
                          <div className="description">
                            {renderDescription(event.description)}
                          </div>
                        )}
                        {event.links && event.links.length > 0 && (
                          <div className="event-links">
                            <h3>Important Links</h3>
                            <div className="links-container">
                              {event.links.map((link, index) => (
                                <a 
                                  key={index}
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`event-link ${link.type || 'default'}`}
                                >
                                  {link.icon && <span className="link-icon">{link.icon}</span>}
                                  {link.label || 'Event Link'}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CalendarCard>
                  );
                })}
              </CalendarContainer>
            )}
          </EventsSection>
        </ContentWrapper>
      </WhiteBackgroundWrapper>
    </PageContainer>
  );
};

export default Events;
